import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { IGeoJSONCircle, IReporteBase } from 'modelos/src';
import { Stop } from 'modelos/src';
import {
  ContextoBusqueda,
  MapInstanceFilter,
  SearchType,
  SeleccionadosBarraLateral,
} from '../interfaces';
import { FiltrosVehiculoComponent } from './filtros/filtros-vehiculos.component';
import { BusquedaZonaComponent } from './busqueda-especifica/busqueda-zona/busqueda-zona.component';
import { BusquedaVehiculoRecorridosComponent } from './busqueda-especifica/recorridos/recorridos.component';
import { MapaVehiculosService } from '../mapaVehiculos.service';
import { ClientesJerarquiaService } from './services/clientes-jerarquia.service';
import { ServiceGeneralVehiculos } from '../mapas-individuales/services/service-general';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Subscription } from 'rxjs';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-barra-lateral',
  templateUrl: './barra-lateral.component.html',
  styleUrls: ['./barra-lateral.component.scss'],
  standalone: false,
})
export class BarraLateralComponent implements OnInit, OnChanges, OnDestroy {
  private formularios = inject(FormularioDialogService);
  mapaService = inject(MapaVehiculosService);
  helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);
  private serviceGeneral = inject(ServiceGeneralVehiculos);
  private jerarquia = inject(ClientesJerarquiaService);

  // Indica si el vehículo actual es el seguido
  // ViewChild references para poder llamar a los métodos resetear()
  @ViewChild(FiltrosVehiculoComponent)
  filtrosComponent?: FiltrosVehiculoComponent;
  @ViewChild(BusquedaZonaComponent)
  busquedaZonaComponent?: BusquedaZonaComponent;
  @ViewChild(BusquedaVehiculoRecorridosComponent)
  busquedaRecorridosComponent?: BusquedaVehiculoRecorridosComponent;

  // INPUTS que aún vienen del contenedor (props no centralizados)
  @Input() mapasPersonalizados?: any[];
  @Input() tipoMapaBaseKey?: string;

  // Subscripciones
  private subs = new Subscription();

  // Propiedad para indicar si hay filtros activos (se actualiza solo cuando cambian los filtros)
  public hasFiltrosActivos: boolean = false;

  // Búsqueda de vehículos (por mapa)
  private busquedaPorMapa = new Map<number, string>();
  private busquedaTimeout?: ReturnType<typeof setTimeout>;
  public textoBusqueda: string = '';
  public coincidenciasBusqueda: number = 0;

  // Getters que acceden al servicio
  get vehiculos(): number {
    return this.filteredReportes.length;
  }

  // Total de vehículos sin filtrar (para mostrar formato parcial/total)
  get totalVehiculos(): number {
    return this.mapaService.reportes?.length ?? 0;
  }

  get reportes(): IReporteBase<'Tracker 4G'>[] {
    return this.mapaService.reportes;
  }

  // Sets memoizados por referencia del array de ids. filteredReportes es un
  // getter que el template evalúa en cada detección de cambios: con includes()
  // sobre ~1300 ids por cada uno de ~1300 reportes eran ~1,8 M comparaciones por
  // evaluación, y al cambiar la capa base (decenas de ciclos seguidos) el mapa
  // se congelaba varios segundos.
  private setsPorArray = new WeakMap<string[], Set<string>>();

  private comoSet(ids: string[]): Set<string> {
    let set = this.setsPorArray.get(ids);
    if (!set) {
      set = new Set(ids);
      this.setsPorArray.set(ids, set);
    }
    return set;
  }

  // Getter que devuelve los reportes filtrados según los filtros activos
  get filteredReportes(): IReporteBase<'Tracker 4G'>[] {
    const allReportes = this.mapaService.reportes;
    const filtros = this.seleccionadoParaEnviar?.filtrosSeleccionados;

    // Si no hay filtros activos, devolver todos los reportes
    if (!filtros) {
      return allReportes;
    }

    // Gate "solo encendidos" (la ignición vive en el reporte). Se aplica al final.
    const aplicarEncendidos = (
      reps: IReporteBase<'Tracker 4G'>[],
    ): IReporteBase<'Tracker 4G'>[] =>
      filtros.soloEncendidos
        ? reps.filter((r) => (r?.valores as any)?.ignicion === true)
        : reps;

    // Si hay idsVehiculosFiltrados (resultado del filtrado completo), usar esos
    if (
      filtros.idsVehiculosFiltrados &&
      filtros.idsVehiculosFiltrados.length > 0
    ) {
      const idsFiltrados = this.comoSet(filtros.idsVehiculosFiltrados);
      return aplicarEncendidos(
        allReportes.filter(
          (r) => r.activo?._id && idsFiltrados.has(r.activo._id),
        ),
      );
    }

    // Caso contrario, aplicar filtros manualmente
    let reportesFiltrados = [...allReportes];

    // Filtro por clientes
    if (
      filtros.idsClientesVehiculos &&
      filtros.idsClientesVehiculos.length > 0
    ) {
      const idsClientes = this.comoSet(filtros.idsClientesVehiculos);
      reportesFiltrados = reportesFiltrados.filter(
        (r) => r.activo?.idCliente && idsClientes.has(r.activo.idCliente),
      );
    }

    // Filtro por grupos
    if (filtros.gruposVehiculos && filtros.gruposVehiculos.length > 0) {
      reportesFiltrados = reportesFiltrados.filter(
        (r) =>
          r.activo?.idGrupo &&
          filtros.gruposVehiculos!.includes(r.activo.idGrupo),
      );
    }

    // Filtro por funciones
    if (filtros.funcionVehiculos && filtros.funcionVehiculos.length > 0) {
      reportesFiltrados = reportesFiltrados.filter(
        (r) =>
          r.activo?.funcion &&
          filtros.funcionVehiculos!.includes(r.activo.funcion),
      );
    }

    // Filtro por tipos de vehículo
    if (filtros.tiposVehiculo && filtros.tiposVehiculo.length > 0) {
      reportesFiltrados = reportesFiltrados.filter(
        (r) =>
          r.activo?.vehiculo?.tipo &&
          filtros.tiposVehiculo!.includes(r.activo.vehiculo.tipo),
      );
    }

    // Filtro por vehículos específicos
    if (filtros.idsVehiculos && filtros.idsVehiculos.length > 0) {
      const idsVehiculos = this.comoSet(filtros.idsVehiculos);
      reportesFiltrados = reportesFiltrados.filter(
        (r) => r.activo?._id && idsVehiculos.has(r.activo._id),
      );
    }

    return aplicarEncendidos(reportesFiltrados);
  }

  get cantMapas(): number {
    return this.mapaService.cantMapas;
  }

  set cantMapas(value: number) {
    this.mapaService.setCantMapas(value);
  }

  get mapaSeleccionado(): number {
    return this.mapaService.mapaSeleccionado;
  }

  set mapaSeleccionado(value: number) {
    this.mapaService.setMapaSeleccionado(value);
  }

  get tipoMapaBase(): string | undefined {
    return this.mapaService.tipoMapaBase;
  }

  get paradaSeleccionada(): Stop | undefined {
    return this.mapaService.paradaSeleccionada;
  }

  get circuloBusqueda(): IGeoJSONCircle | undefined {
    return this.mapaService.circuloBusqueda;
  }

  get seleccionadosGuardados(): SeleccionadosBarraLateral[] {
    return this.mapaService.seleccionadosBarraLateral;
  }

  // Propiedad local para el reporte actual (se actualiza via suscripciones)
  // IMPORTANTE: No usar getter porque no dispara ngOnChanges en componentes hijos
  public reporteLocal: IReporteBase<'Tracker 4G'> | undefined;

  // Getter que mantiene retrocompatibilidad (usado en algunos lugares del template)
  public get reporte(): IReporteBase<'Tracker 4G'> | undefined {
    return this.reporteLocal;
  }

  // Setter para actualizar el reporte por mapa
  public set reporte(val: IReporteBase<'Tracker 4G'> | undefined) {
    this.reporteLocal = val;
    this.mapaService.setReporteSeleccionadoPorMapa(this.mapaSeleccionado, val);
  }

  // OUTPUTS mantenidos para eventos específicos
  @Output() reporteChange = new EventEmitter<
    IReporteBase<'Tracker 4G'> | undefined
  >();
  @Output() tipoMapaBaseChange = new EventEmitter<string>();
  @Output() toggleHelpChange = new EventEmitter<void>();
  @Output() busquedaVehiculos = new EventEmitter<{
    reportes: IReporteBase<'Tracker 4G'>[];
    mapIndex: number;
    clearSearch?: boolean; // true cuando se limpia la búsqueda (mostrar todos), false/undefined cuando hay búsqueda activa
  }>();

  // Estado local del componente
  public verDetallesReporte: boolean = false;
  /** Última zona que abrió la sección, para no re-abrirla en cada emisión. */
  private _ultimaUbicacionAbierta?: string;
  /** Búsqueda por dirección desplegada (sólo aplica sin nada seleccionado). */
  public busquedaDireccionOpen = false;
  public filtrosOpen: boolean = false;
  public busquedaSectionOpen: boolean = false;
  public gestionMapasOpen: boolean = true;
  public detallesVehiculoOpen: boolean = false;
  public showHelp: boolean = false;
  public seleccionadoParaEnviar?: SeleccionadosBarraLateral;

  // Getter para acceder al array del servicio
  get seleccionadosBarraLateral(): SeleccionadosBarraLateral[] {
    return this.mapaService.seleccionadosBarraLateral;
  }

  // Toggles de visibilidad
  public mostrarVehiculos: boolean = true;
  public mostrarUbicaciones: boolean = false;
  public mostrarNombreUbicaciones: 'ninguno' | 'todos' = 'ninguno';
  public mostrarRecorridos: 'ninguno' | 'todos' | 'seleccionado' = 'ninguno';
  public mostrarNombreRecorridos: 'ninguno' | 'todos' = 'ninguno';
  public mostrarNombreVehiculos: 'ninguno' | 'todos' | 'seleccionado' = 'todos';
  public categoriaMarker: 'tipo' | 'funcion' = 'tipo';
  public mostrarFoto: 'no' | 'centrado' | 'completo' = 'no';
  public escalaIconos: number = 1; // Escala de iconos (0.5 a 2)
  public iconPitchAlignment: 'viewport' | 'map' = 'viewport'; // Alineación del pitch de los íconos
  public seguimientoActivo: boolean = false; // Toggle de seguimiento de vehículo (por defecto inactivo)
  public animacionesActivas: boolean = false; // Toggle de animaciones de markers (por defecto inactivo)
  public usarCoordenadasSnapped: boolean = true; // Toggle de coordenadas snapped (por defecto activo)

  // Opciones para los selectores
  public mapOptionsCount: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  public mapOptionsSelected: number[] = [1];

  async ngOnInit() {
    // Suscribirse a cambios en el servicio
    this.subs.add(
      this.mapaService.cantMapas$.subscribe(() => {
        this.updateMapOptions();
      }),
    );

    // Cuando cambia el mapa seleccionado, restaurar TODO su estado
    this.subs.add(
      this.mapaService.mapaSeleccionado$.subscribe(async () => {
        await this.restaurarEstadoCompleto();
      }),
    );

    // Suscribirse a cambios en los filtros
    this.subs.add(
      this.mapaService.filtrosPorMapa$.subscribe((filtrosPorMapa) => {
        const filtros = filtrosPorMapa[this.mapaSeleccionado];
        this.actualizarIndicadorFiltrosActivos(filtros);
        this.updateSeleccionadosBarraLateral({
          filtrosSeleccionados: filtros,
          hasFiltrosActivos: this.hasFiltrosActivos,
        });
      }),
    );

    // Re-aplicar búsqueda de texto cuando los reportes cargan por primera vez.
    // Esto soluciona el caso donde restaurarBusquedaParaMapa() se ejecuta antes de
    // que los reportes estén disponibles (race condition en el reload de página).
    this.subs.add(
      this.mapaService.reportes$.subscribe((reportes) => {
        if (reportes.length > 0 && this.textoBusqueda) {
          const mapIndex = this.mapaSeleccionado - 1;
          this.ejecutarBusqueda(this.textoBusqueda, mapIndex);
        }
      }),
    );

    // Suscribirse a cambios en el reporte seleccionado (actualización en tiempo real via WebSocket)
    this.subs.add(
      this.mapaService.reporteSeleccionadoPorMapa$.subscribe(
        (reportesPorMapa) => {
          const reporteActualizado = reportesPorMapa[this.mapaSeleccionado];
          // Solo actualizar si el ID del activo cambió (evita re-renders innecesarios)
          if (
            reporteActualizado &&
            reporteActualizado.activo?._id !== this.reporteLocal?.activo?._id
          ) {
            const previousId = this.reporteLocal?.activo?._id;
            // Actualizar reporteLocal ANTES de onVehiculoCambiado para evitar re-entrancia
            this.reporteLocal = reporteActualizado;
            this.onVehiculoCambiado(previousId, reporteActualizado.activo?._id);
            this.cdr.detectChanges();
          } else if (reporteActualizado) {
            // Mismo vehículo: actualización en vivo (WS). Refrescar la referencia
            // y forzar CD para que el card muestre los valores nuevos (velocidad, etc.).
            this.reporteLocal = reporteActualizado;
            this.cdr.detectChanges();
          }
        },
      ),
    );

    // Suscribirse a cambios en seleccionadosBarraLateral para detectar actualizaciones del polling HTTP
    this.subs.add(
      this.mapaService.seleccionadosBarraLateral$.subscribe((seleccionados) => {
        const seleccionadoActual = seleccionados.find(
          (s) => s.mapaSeleccionado === this.mapaSeleccionado,
        );

        // Actualizar indicador de filtros activos cuando cambien los filtros
        if (seleccionadoActual?.filtrosSeleccionados) {
          this.actualizarIndicadorFiltrosActivos(
            seleccionadoActual.filtrosSeleccionados,
          );
        }

        // Zona seleccionada (click en el mapa) → abrir la sección de búsqueda
        // ya posicionada en el contexto de esa zona. El guard evita re-abrirla
        // en cada emisión y corta la re-entrancia de sincronizarContexto().
        const idUbicacionSel = seleccionadoActual?.ubicacionSeleccionada?._id;
        if (!idUbicacionSel) {
          if (this._ultimaUbicacionAbierta) {
            this._ultimaUbicacionAbierta = undefined;
            this.sincronizarContexto();
            this.cdr.detectChanges();
          }
        } else if (idUbicacionSel !== this._ultimaUbicacionAbierta) {
          this._ultimaUbicacionAbierta = idUbicacionSel;
          this.busquedaSectionOpen = true;
          this.sincronizarContexto();
          this.guardarEstadoCompleto();
          this.cdr.detectChanges();
        }

        // Solo actualizar si el ID del activo cambió (evita re-renders innecesarios)
        if (
          seleccionadoActual?.reporteSeleccionado &&
          seleccionadoActual.reporteSeleccionado.activo?._id !==
            this.reporteLocal?.activo?._id
        ) {
          const previousId = this.reporteLocal?.activo?._id;
          // Actualizar reporteLocal ANTES de onVehiculoCambiado para evitar re-entrancia
          this.reporteLocal = seleccionadoActual.reporteSeleccionado;
          this.onVehiculoCambiado(
            previousId,
            seleccionadoActual.reporteSeleccionado.activo?._id,
          );
          this.cdr.detectChanges();
        }
        // NOTA: para el MISMO vehículo NO se pisa reporteLocal acá. Este canal
        // (seleccionadosBarraLateral$) puede re-emitir con un reporteSeleccionado
        // más viejo (p. ej. tras updateSeleccionadoBarraLateral desde el card),
        // lo que pisaría el dato en vivo. La actualización en vivo del mismo
        // vehículo llega por reporteSeleccionadoPorMapa$ (WS + poll de backup).

        // Va al final: recién acá reporteLocal y los filtros son los de esta
        // emisión, y se puede decidir si el vehículo elegido sigue estando.
        this.soltarVehiculoFueraDeFiltro(seleccionadoActual);
      }),
    );

    this.updateMapOptions();
    // Restaurar estado inicial
    await this.restaurarEstadoCompleto();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    // Limpiar timeout de búsqueda si existe
    if (this.busquedaTimeout) {
      clearTimeout(this.busquedaTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Los estados guardados ahora vienen del servicio automáticamente
    // La sincronización se hace en ngOnInit a través de las suscripciones

    if (changes['cantMapas']) {
      this.updateMapOptions();
    }

    if (changes['mapaSeleccionado']) {
      // La sincronización ahora se maneja en ngOnInit a través de las suscripciones
    }

    // Detectar cambios en tipoMapaBase para sincronizar con el selector
    if (changes['tipoMapaBase'] && !changes['tipoMapaBase'].firstChange) {
    }
  }

  private updateMapOptions() {
    this.mapOptionsCount = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    const arr: number[] = [];
    for (let i = 1; i <= Math.max(1, this.cantMapas); i++) {
      arr.push(i);
    }
    this.mapOptionsSelected = arr;

    if (this.mapaSeleccionado > this.cantMapas) {
      this.mapaService.setMapaSeleccionado(this.cantMapas);
    }
  }

  // Devuelve la clase maps-X usada por el CSS
  public get mapsClass(): string {
    const n = Math.max(1, Math.min(9, Math.floor(Number(this.cantMapas) || 1)));
    return `maps-${n}`;
  }

  // Cambio del mapa seleccionado (dots)
  public selectMapaByDot(m: number) {
    const newM = Math.max(
      1,
      Math.min(this.cantMapas, Math.floor(Number(m) || 1)),
    );
    if (this.mapaSeleccionado === newM) return;
    this.mapaService.setMapaSeleccionado(newM);
  }

  // --- handlers existentes (mantenidos) ---
  public handleCantMapasChange(cant: number) {
    const newCant = Math.max(1, Math.min(9, Math.floor(Number(cant) || 1)));
    this.mapaService.setCantMapas(newCant);

    if (this.mapaSeleccionado > newCant) {
      this.mapaService.setMapaSeleccionado(newCant);
    }

    this.updateMapOptions();
  }

  /**
   * Detecta cambio de vehículo y limpia todas las búsquedas específicas.
   * Usa un flag para evitar re-entrancia (las llamadas a resetear() emiten eventos
   * que disparan subscripciones que podrían llamar a este método recursivamente).
   */
  private _resettingSearches = false;
  private onVehiculoCambiado(previousId?: string, newId?: string): void {
    if (this._resettingSearches) return;
    if (previousId === newId) return;

    if (previousId) {
      this._resettingSearches = true;

      // Limpiar todas las búsquedas específicas
      // Zona: solo limpiar resultados, preservar el círculo/polígono de búsqueda
      this.busquedaZonaComponent?.limpiarResultados();
      this.busquedaRecorridosComponent?.resetear();

      this.updateSeleccionadosBarraLateral({
        puedeDibujar: false,
        seleccionadosVehiculosRecorridos: undefined,
      });

      // Liberar el flag después de que la cadena síncrona termine
      setTimeout(() => {
        this._resettingSearches = false;
      }, 0);
    }

    // El contexto se sincroniza con cualquier cambio de vehículo, también
    // cuando antes no había ninguno: tras soltar el vehículo el estado queda en
    // tipoBusqueda 'zona', y el mapa lee ese campo para saber qué dibujar. Sin
    // esto, el recorrido del vehículo elegido después de soltar no se dibujaba.
    this.sincronizarContexto();
  }

  public handleReporteChange(r?: IReporteBase<'Tracker 4G'>) {
    const previousId = this.reporte?.activo?._id;
    this.onVehiculoCambiado(previousId, r?.activo?._id);
    this.reporte = r;

    // Preparar actualizaciones del estado
    const updates: Partial<SeleccionadosBarraLateral> = {
      reporteSeleccionado: r,
    };

    // Si el seguimiento está activo y hay un vehículo seleccionado, seguirlo automáticamente
    if (this.seguimientoActivo && r && r.activo?._id) {
      updates.followTargetId = r.activo._id;
    }

    // Guardar el reporte seleccionado en el estado de barra lateral
    this.updateSeleccionadosBarraLateral(updates);
    this.reporteChange.emit(r);

    // El detalle no se abre solo al seleccionar: lo despliega el usuario.
    // Sin vehículo sí se cierra, porque no hay nada que mostrar.
    if (!r) {
      this.detallesVehiculoOpen = false;
    }

    // Seleccionar o soltar el vehículo cambia el contexto del panel
    if (!r || r.activo?._id !== previousId) {
      this.sincronizarContexto();
    }
  }

  public onToggleSeguimiento() {
    // No hacer toggle manual - ngModel ya lo hace automáticamente

    // Si se activa el seguimiento y hay un vehículo seleccionado, seguirlo
    if (this.seguimientoActivo && this.reporte && this.reporte.activo?._id) {
      this.updateSeleccionadosBarraLateral({
        followTargetId: this.reporte.activo._id,
        seguimientoActivo: this.seguimientoActivo,
      });
    } else if (!this.seguimientoActivo) {
      // Si se desactiva el seguimiento, limpiar el targetId
      this.updateSeleccionadosBarraLateral({
        followTargetId: undefined,
        seguimientoActivo: this.seguimientoActivo,
      });
    } else {
      // Si se activa pero no hay vehículo seleccionado, solo guardar el estado
      this.updateSeleccionadosBarraLateral({
        seguimientoActivo: this.seguimientoActivo,
      });
    }
  }

  public handleReporteVerChange(r: boolean) {
    this.verDetallesReporte = r;
  }

  // Método privado para actualizar el indicador de filtros activos
  private actualizarIndicadorFiltrosActivos(filtros?: MapInstanceFilter): void {
    const anteriorEstado = this.hasFiltrosActivos;

    if (!filtros) {
      this.hasFiltrosActivos = false;
    } else {
      // El filtro de clientes arranca siempre con el cliente actual del permiso:
      // sólo cuenta como "activo" si el usuario se movió de ese default.
      const idsClientes =
        filtros.idsClientes ?? filtros.idsClientesVehiculos ?? [];
      const esDefaultClientes =
        !!filtros.includeChildren &&
        idsClientes.length === 1 &&
        idsClientes[0] === ClientesJerarquiaService.idClienteActual();
      const tieneClientes = !esDefaultClientes;
      const tieneGrupos =
        filtros.gruposVehiculos && filtros.gruposVehiculos.length > 0;
      const tieneFunciones =
        filtros.funcionVehiculos && filtros.funcionVehiculos.length > 0;
      const tieneTipos =
        filtros.tiposVehiculo && filtros.tiposVehiculo.length > 0;
      const tieneVehiculos =
        filtros.idsVehiculos && filtros.idsVehiculos.length > 0;
      const tieneEncendidos = !!filtros.soloEncendidos;

      this.hasFiltrosActivos =
        tieneClientes ||
        tieneGrupos ||
        tieneFunciones ||
        tieneTipos ||
        tieneVehiculos ||
        tieneEncendidos;
    }

    // Si cambió el estado, forzar actualización visual
    if (anteriorEstado !== this.hasFiltrosActivos) {
      this.cdr.detectChanges();
    }
  }

  // CONTEXTO DE BÚSQUEDA
  // Ya no hay solapas: lo que se muestra sale de lo que está seleccionado.
  // La zona gana sobre el vehículo porque con ambos la búsqueda que aplica
  // (visitas del vehículo a esa zona) es de zona.
  get contextoBusqueda(): ContextoBusqueda {
    if (this.seleccionadoParaEnviar?.ubicacionSeleccionada) return 'ubicacion';
    if (this.reporteLocal) return 'vehiculo';
    return 'sin-seleccion';
  }

  /** Zona y vehículo se muestran por separado: pueden estar los dos. */
  get tituloUbicacionSeleccionada(): string | null {
    const ubicacion = this.seleccionadoParaEnviar?.ubicacionSeleccionada;
    if (!ubicacion) return null;
    return ubicacion.identificacion || 'Zona seleccionada';
  }

  get tituloVehiculoSeleccionado(): string | null {
    if (!this.reporteLocal) return null;
    return this.reporteLocal.activo?.identificacion || 'Vehículo seleccionado';
  }

  /** Suelta la zona y deja el panel en el contexto del vehículo (o sin nada). */
  public limpiarUbicacionSeleccionada(): void {
    this.updateSeleccionadosBarraLateral({
      ubicacionSeleccionada: undefined,
    });
    this.mapaService.setPoligonoBusqueda(undefined);
    this.sincronizarContexto();
    this.guardarEstadoCompleto();
    this.cdr.detectChanges();
  }

  public limpiarVehiculoSeleccionado(): void {
    this.handleReporteChange(undefined);
    this.sincronizarContexto();
    this.guardarEstadoCompleto();
    this.cdr.detectChanges();
  }

  /**
   * Suelta el vehículo seleccionado cuando los filtros dejan de incluirlo (por
   * ejemplo al cambiar de cliente). El marker desaparece del mapa, así que su
   * recorrido dibujado y el panel en su contexto quedarían huérfanos: se lo
   * suelta igual que con el botón del chip, que ya limpia la búsqueda.
   */
  private soltarVehiculoFueraDeFiltro(
    seleccionado?: SeleccionadosBarraLateral,
  ): void {
    const reporte = this.reporteLocal;
    if (!reporte || !seleccionado?.filtrosSeleccionados) return;

    // Misma regla que usa el mapa para mostrar el marker, para no soltar un
    // vehículo que sigue estando a la vista.
    const visible = this.serviceGeneral.shouldShowReport(
      reporte,
      this.mapaSeleccionado - 1,
      seleccionado,
    );
    if (visible) return;

    this.limpiarVehiculoSeleccionado();
  }

  /** Tipo de búsqueda que el mapa usa para saber qué dibujar. */
  private tipoBusquedaDeContexto(): SearchType {
    return this.contextoBusqueda === 'vehiculo' ? 'recorrido' : 'zona';
  }

  /**
   * Alinea el estado que consume el mapa (tipo de búsqueda y permisos de
   * dibujo) con el contexto actual. Reemplaza a lo que antes hacía toggleTab.
   */
  private sincronizarContexto(): void {
    const contexto = this.contextoBusqueda;
    this.updateSeleccionadosBarraLateral({
      tipoBusqueda: this.tipoBusquedaDeContexto(),
      // El círculo libre sólo aplica sin nada seleccionado y con la búsqueda
      // por dirección desplegada: si no, el botón del mapa sobra.
      puedeDibujar: contexto === 'sin-seleccion' && this.busquedaDireccionOpen,
      puedeDibujarRecorrido: contexto === 'vehiculo',
    });
  }

  public toggleBusquedaDireccion(): void {
    this.busquedaDireccionOpen = !this.busquedaDireccionOpen;
    this.sincronizarContexto();
    this.guardarEstadoCompleto();
  }

  // AUXILIARES UI
  public abrirDialogFiltros() {
    // Limpiar la búsqueda antes de abrir el diálogo de filtros
    this.limpiarBusqueda();

    const dialogRef = this.formularios.abrir(FiltrosVehiculoComponent, {
      tamano: 'mediano',
      data: {
        seleccionadoBarraLateral: this.seleccionadoParaEnviar,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      // Después de cerrar el diálogo, actualizar el indicador de filtros activos
      const filtrosActuales = this.seleccionadoParaEnviar?.filtrosSeleccionados;
      this.actualizarIndicadorFiltrosActivos(filtrosActuales);

      // Actualizar la UI después de cerrar el dialog
      this.cdr.detectChanges();
    });
  }

  public toggleBusquedaSection() {
    this.busquedaSectionOpen = !this.busquedaSectionOpen;

    // Al abrir, alinear tipoBusqueda/puedeDibujar con el contexto: el mapa lee
    // tipoBusqueda para saber qué dibujar (esCombinacionCirculo) y sin esto
    // guardarEstadoCompleto lo dejaría inconsistente.
    if (this.busquedaSectionOpen) {
      this.sincronizarContexto();
    }
    this.guardarEstadoCompleto();
  }

  public toggleGestionMapas() {
    this.gestionMapasOpen = !this.gestionMapasOpen;
    this.guardarEstadoCompleto();
  }

  public toggleDetallesVehiculo() {
    this.detallesVehiculoOpen = !this.detallesVehiculoOpen;
    this.guardarEstadoCompleto();
  }

  public onTipoMapaBaseChange(tipo: string) {
    this.tipoMapaBaseChange.emit(tipo);
  }

  public onBusquedaChange(texto: string) {
    // Cancelar búsqueda anterior si existe
    if (this.busquedaTimeout) {
      clearTimeout(this.busquedaTimeout);
    }

    // Guardar el texto de búsqueda para este mapa
    this.guardarBusquedaParaMapa(texto);

    const mapIndex = this.mapaSeleccionado - 1;

    // Si el texto está vacío, ejecutar inmediatamente
    if (!texto || texto.trim() === '') {
      this.coincidenciasBusqueda = 0;
      this.busquedaVehiculos.emit({
        reportes: [],
        mapIndex,
        clearSearch: true,
      });
      return;
    }

    // Esperar 400ms antes de ejecutar la búsqueda (debounce)
    this.busquedaTimeout = setTimeout(() => {
      this.ejecutarBusqueda(texto, mapIndex);
    }, 400);
  }

  private ejecutarBusqueda(texto: string, mapIndex: number) {
    const terminoBusqueda = texto.toLowerCase().trim();
    const reportesFiltrados = this.filteredReportes.filter((reporte) => {
      // Convertir el reporte completo a string y buscar
      const textoReporte = JSON.stringify(reporte).toLowerCase();
      return textoReporte.includes(terminoBusqueda);
    });

    this.coincidenciasBusqueda = reportesFiltrados.length;
    this.busquedaVehiculos.emit({ reportes: reportesFiltrados, mapIndex });

    // Si solo hay una coincidencia, seleccionar ese vehículo
    if (reportesFiltrados.length === 1) {
      this.handleReporteChange(reportesFiltrados[0]);
    }
  }

  public limpiarBusqueda() {
    this.textoBusqueda = '';
    this.coincidenciasBusqueda = 0;
    this.guardarBusquedaParaMapa('');
    const mapIndex = this.mapaSeleccionado - 1;
    this.busquedaVehiculos.emit({ reportes: [], mapIndex, clearSearch: true });
  }

  public toggleHelp() {
    this.showHelp = !this.showHelp;
    this.toggleHelpChange.emit();
  }

  public closeDetalleReporte() {
    this.verDetallesReporte = false;
  }

  // MÉTODOS DE RESETEO
  /**
   * Resetea una búsqueda específica según su tipo
   * @param tipo El tipo de búsqueda a resetear
   */
  private resetearBusquedaPorTipo(tipo: SearchType): void {
    switch (tipo) {
      case 'zona':
        this.busquedaZonaComponent?.resetear();
        this.updateSeleccionadosBarraLateral({
          seleccionadosVehiculoZona: undefined,
        });
        break;
      case 'recorrido':
        this.busquedaRecorridosComponent?.resetear();
        this.updateSeleccionadosBarraLateral({
          seleccionadosVehiculosRecorridos: undefined,
        });
        break;
    }
  }

  /**
   * Resetea las búsquedas que NO sean la especificada
   * @param panelActivo El panel que debe mantenerse activo (no se resetea)
   */
  private resetearOtrasBusquedas(panelActivo: SearchType): void {
    // Resetear búsqueda por zona si no es la activa
    if (panelActivo !== 'zona') {
      this.busquedaZonaComponent?.resetear();
    }

    // Resetear búsqueda por recorrido si no es la activa
    if (panelActivo !== 'recorrido') {
      this.busquedaRecorridosComponent?.resetear();
    }

    // Actualizar el estado eliminando las búsquedas no activas
    const updates: Partial<
      Omit<SeleccionadosBarraLateral, 'mapaSeleccionado'>
    > = {};

    if (panelActivo !== 'zona') {
      updates.seleccionadosVehiculoZona = undefined;
    }
    if (panelActivo !== 'recorrido') {
      updates.seleccionadosVehiculosRecorridos = undefined;
    }

    // Solo actualizar si hay cambios
    if (Object.keys(updates).length > 0) {
      this.updateSeleccionadosBarraLateral(updates);
    }
  }

  /**
   * Resetea únicamente los componentes de búsqueda específica
   */
  public resetearBusqueda(): void {
    // Cerrar todas las búsquedas activas
    this.busquedaSectionOpen = false;
    this._ultimaUbicacionAbierta = undefined;

    // Llamar a resetear() en cada componente de búsqueda
    this.busquedaZonaComponent?.resetear();
    this.busquedaRecorridosComponent?.resetear();

    // Actualizar el estado de seleccionados
    this.updateSeleccionadosBarraLateral({
      tipoBusqueda: null,
      puedeDibujar: false,
      puedeDibujarRecorrido: false,
      seleccionadosVehiculoZona: undefined,
      seleccionadosVehiculosRecorridos: undefined,
    });
  }

  /**
   * Resetea TODOS los filtros y búsquedas
   */
  public async resetearTodo(): Promise<void> {
    // Resetear filtros
    this.filtrosComponent?.limpiarFiltros();

    // Resetear búsquedas
    this.resetearBusqueda();

    // Limpiar búsqueda de vehículos
    this.limpiarBusqueda();

    // Volver al filtro inicial (cliente actual del permiso y su jerarquía) en
    // vez de dejar el mapa sin filtro, que mostraría todo el scope del permiso.
    const filtroPorDefecto = await this.jerarquia.filtroPorDefecto();
    this.hasFiltrosActivos = false;
    this.mapaService.setFiltrosActual(filtroPorDefecto);
    this.updateSeleccionadosBarraLateral({
      hasFiltrosActivos: false,
      filtrosSeleccionados: filtroPorDefecto,
    });

    // Forzar detección de cambios para actualizar la UI
    this.cdr.detectChanges();
  }

  // FUNCIÓN CLAVE: actualiza seleccionados usando el servicio
  private updateSeleccionadosBarraLateral(
    changes: Partial<Omit<SeleccionadosBarraLateral, 'mapaSeleccionado'>>,
  ) {
    // El servicio maneja internamente qué mapa está activo
    this.mapaService.updateSeleccionadoBarraLateral(changes);
    this.seleccionadoParaEnviar = this.getSeleccionadoActual();
  }

  // Obtiene el seleccionado "actual" para el mapa activo
  private getSeleccionadoActual(): SeleccionadosBarraLateral | undefined {
    const seleccionado = this.seleccionadosBarraLateral.find(
      (s) => s.mapaSeleccionado === this.mapaSeleccionado,
    );
    if (seleccionado) return seleccionado;
    return undefined;
  }

  /**
   * Guarda TODO el estado actual de la barra lateral en el servicio
   */
  private guardarEstadoCompleto(): void {
    const contexto = this.contextoBusqueda;

    const estadoActual: Partial<SeleccionadosBarraLateral> = {
      tipoBusqueda: this.tipoBusquedaDeContexto(),
      // El círculo libre sólo aplica sin nada seleccionado y con la búsqueda
      // por dirección abierta: con una zona elegida el mapa dibuja su polígono.
      puedeDibujar:
        this.busquedaSectionOpen &&
        contexto === 'sin-seleccion' &&
        this.busquedaDireccionOpen,
      puedeDibujarRecorrido:
        this.busquedaSectionOpen && contexto === 'vehiculo',
      mostrarVehiculos: this.mostrarVehiculos,
      mostrarUbicaciones: this.mostrarUbicaciones,
      mostrarNombreUbicaciones: this.mostrarNombreUbicaciones,
      mostrarRecorridos: this.mostrarRecorridos,
      mostrarNombreRecorridos: this.mostrarNombreRecorridos,
      mostrarNombreVehiculos: this.mostrarNombreVehiculos,
      categoriaMarker: this.categoriaMarker,
      mostrarFoto: this.mostrarFoto,
      escalaIconos: this.escalaIconos,
      iconPitchAlignment: this.iconPitchAlignment,
      seguimientoActivo: this.seguimientoActivo,
      animacionesActivas: this.animacionesActivas,
      usarCoordenadasSnapped: this.usarCoordenadasSnapped,
      reporteSeleccionado: this.reporte,
      hasFiltrosActivos: this.hasFiltrosActivos,
      // Estados de apertura/cierre de sliders
      gestionMapasOpen: this.gestionMapasOpen,
      filtrosOpen: this.filtrosOpen,
      detallesVehiculoOpen: this.detallesVehiculoOpen,
      busquedaSectionOpen: this.busquedaSectionOpen,
    };

    // El servicio maneja internamente qué mapa está activo
    this.mapaService.updateSeleccionadoBarraLateral(estadoActual);
  }

  /**
   * Restaura TODO el estado de la barra lateral desde el servicio
   */
  private async restaurarEstadoCompleto() {
    const seleccionado = this.mapaService.getSeleccionadoActual();

    // Restaurar reporte local (necesario para disparar ngOnChanges en componentes hijos)
    this.reporteLocal = seleccionado?.reporteSeleccionado;

    // Restaurar toggles de visibilidad
    this.mostrarVehiculos = seleccionado?.mostrarVehiculos ?? true;
    this.mostrarUbicaciones = seleccionado?.mostrarUbicaciones ?? false;
    this.mostrarNombreUbicaciones =
      seleccionado?.mostrarNombreUbicaciones ?? 'ninguno';
    this.mostrarRecorridos = seleccionado?.mostrarRecorridos ?? 'ninguno';
    this.mostrarNombreRecorridos =
      seleccionado?.mostrarNombreRecorridos ?? 'ninguno';
    this.mostrarNombreVehiculos =
      seleccionado?.mostrarNombreVehiculos ?? 'todos';
    this.categoriaMarker = seleccionado?.categoriaMarker ?? 'tipo';
    this.mostrarFoto = seleccionado?.mostrarFoto ?? 'no';
    this.escalaIconos = seleccionado?.escalaIconos ?? 1;
    this.iconPitchAlignment = seleccionado?.iconPitchAlignment ?? 'viewport';
    this.seguimientoActivo = seleccionado?.seguimientoActivo ?? false;
    this.animacionesActivas = seleccionado?.animacionesActivas ?? true;
    this.usarCoordenadasSnapped = seleccionado?.usarCoordenadasSnapped ?? true;

    // Restaurar sección abierta (el contenido sale del contexto, no del estado)
    this.busquedaSectionOpen = seleccionado?.busquedaSectionOpen ?? false;
    this._ultimaUbicacionAbierta = seleccionado?.ubicacionSeleccionada?._id;

    // Actualizar el objeto que se envía a los hijos
    this.seleccionadoParaEnviar = seleccionado;

    // Restaurar indicador de filtros activos desde el servicio
    this.hasFiltrosActivos = seleccionado?.hasFiltrosActivos ?? false;

    // Restaurar estados de apertura/cierre de sliders
    this.gestionMapasOpen = seleccionado?.gestionMapasOpen ?? true;
    this.filtrosOpen = seleccionado?.filtrosOpen ?? false;
    this.detallesVehiculoOpen = seleccionado?.detallesVehiculoOpen ?? false;

    // Actualizar tipo de mapa base ANTES del detectChanges para que el selector lo reciba correctamente
    this.actualizarTipoMapaBaseParaMapaSeleccionado();

    // Forzar detección de cambios PRIMERO para que los componentes hijos reciban el reporteSeleccionado
    this.cdr.detectChanges();

    // Esperar al siguiente ciclo de detección de cambios para que los inputs estén actualizados
    await Promise.resolve();

    // AHORA sí, sincronizar elementos de búsqueda (círculos, paradas, etc.)
    await this.syncOpenPanelFromSeleccionado(seleccionado);

    // Restaurar texto de búsqueda para este mapa
    this.restaurarBusquedaParaMapa();
  }

  // Re-ejecuta las búsquedas guardadas del mapa al que se acaba de entrar
  private async syncOpenPanelFromSeleccionado(
    seleccionado?: SeleccionadosBarraLateral,
  ) {
    // El contexto sale de lo seleccionado en ESTE mapa, no del estado guardado
    this.sincronizarContexto();
    const tipo = this.tipoBusquedaDeContexto();

    // Solo re-ejecutar búsqueda de zona si el contexto es de zona con datos guardados
    if (
      tipo === 'zona' &&
      seleccionado?.seleccionadosVehiculoZona?.busquedaVehiculosPorZona
    ) {
      this.busquedaZonaComponent?.buscar();
    }
    // Solo llamar a buscar() en componentes que tienen una búsqueda guardada válida (idVehiculo truthy)
    // Si la búsqueda fue limpiada (idVehiculo null/undefined), no re-ejecutar
    if (seleccionado?.seleccionadosVehiculosRecorridos?.idVehiculo) {
      // Si ya hay datos de búsqueda (trips o polylines), el estado se restaura via
      // la suscripción seleccionadoActual$ en el componente. No re-buscar para
      // evitar que se sobreescriban las polylines del mapa con un array vacío.
      const busquedaExistente =
        seleccionado.seleccionadosVehiculosRecorridos.busquedaRealizada;
      const tieneData =
        busquedaExistente?.polylines?.length ||
        busquedaExistente?.recorridos?.length ||
        busquedaExistente?.ultimos10?.length;
      if (!tieneData) {
        await this.busquedaRecorridosComponent?.buscar();
      }
    }
    // Paradas y eventos ahora salen de los overlays de la búsqueda de viajes:
    // al cambiar de mapa se sueltan y los vuelve a fijar quien los seleccione.
    this.mapaService.setParadaSeleccionada(undefined);
    this.mapaService.setEventoSeleccionado(undefined);

    // NO llamar setCirculoBusqueda(undefined) aquí al restaurar estado.
    // La ocultación visual se maneja en mapa-individual.component ngOnChanges.
    // Llamar setCirculoBusqueda(undefined) destruía el estado persistido del círculo.
  }

  // TOGGLES DE VISIBILIDAD
  public onToggleMostrarVehiculos(): void {
    this.guardarEstadoCompleto();
  }

  public onToggleMostrarUbicaciones(): void {
    this.guardarEstadoCompleto();
  }

  public onToggleMostrarNombreUbicaciones(): void {
    // Ciclar entre los dos estados: ninguno → todos → ninguno
    this.mostrarNombreUbicaciones =
      this.mostrarNombreUbicaciones === 'ninguno' ? 'todos' : 'ninguno';
    this.guardarEstadoCompleto();
  }

  public onToggleMostrarRecorridos(): void {
    // Ciclar entre los tres estados: ninguno → todos → seleccionado → ninguno
    const estados: ('ninguno' | 'todos' | 'seleccionado')[] = [
      'ninguno',
      'todos',
      'seleccionado',
    ];
    const actual = estados.indexOf(this.mostrarRecorridos);
    this.mostrarRecorridos = estados[(actual + 1) % estados.length];
    this.guardarEstadoCompleto();
  }

  /** Texto del tooltip del toggle de recorridos según el estado siguiente. */
  public get tooltipMostrarRecorridos(): string {
    switch (this.mostrarRecorridos) {
      case 'todos':
        return 'Mostrando todos los recorridos. Hacé clic para ver sólo el del vehículo seleccionado';
      case 'seleccionado':
        return 'Mostrando el recorrido del vehículo seleccionado. Hacé clic para ocultarlos';
      default:
        return 'Recorridos ocultos. Hacé clic para mostrarlos todos';
    }
  }

  /** Ícono del toggle de recorridos según el estado. */
  public get iconoMostrarRecorridos(): string {
    return this.mostrarRecorridos === 'seleccionado' ? 'alt_route' : 'route';
  }

  public onToggleMostrarNombreRecorridos(): void {
    // Ciclar entre los dos estados: ninguno → todos → ninguno
    this.mostrarNombreRecorridos =
      this.mostrarNombreRecorridos === 'ninguno' ? 'todos' : 'ninguno';
    this.guardarEstadoCompleto();
  }

  public onToggleMostrarNombreVehiculos(): void {
    // Ciclar entre los tres estados: ninguno → todos → seleccionado → ninguno
    const estados: ('ninguno' | 'todos' | 'seleccionado')[] = [
      'ninguno',
      'todos',
      'seleccionado',
    ];
    const indexActual = estados.indexOf(this.mostrarNombreVehiculos);
    const siguiente = estados[(indexActual + 1) % estados.length];
    this.mostrarNombreVehiculos = siguiente;

    this.guardarEstadoCompleto();
  }

  public onCategoriaMarkerChange(): void {
    // Alternar entre tipo y función
    this.categoriaMarker = this.categoriaMarker === 'tipo' ? 'funcion' : 'tipo';
    this.guardarEstadoCompleto();
  }

  public onMostrarFotoChange(): void {
    if (this.mostrarFoto === 'no') this.mostrarFoto = 'completo';
    else if (this.mostrarFoto === 'completo') this.mostrarFoto = 'centrado';
    else this.mostrarFoto = 'no';
    this.guardarEstadoCompleto();
  }

  public onEscalaIconosChange(escala: number): void {
    this.escalaIconos = escala;
    this.guardarEstadoCompleto();
  }

  public onIconPitchAlignmentChange(alignment: 'viewport' | 'map'): void {
    this.iconPitchAlignment = alignment;
    this.guardarEstadoCompleto();
  }

  public onAnimacionesActivasChange(activo: boolean): void {
    this.animacionesActivas = activo;
    this.guardarEstadoCompleto();
  }

  public onUsarCoordenadasSnappedChange(): void {
    this.usarCoordenadasSnapped = !this.usarCoordenadasSnapped;
    this.guardarEstadoCompleto();
  }

  // Función para formatear el label del slider
  public formatLabel(value: number): string {
    return `${value.toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`;
  }

  /**
   * Guarda el texto de búsqueda para el mapa actual en localStorage y Map
   */
  private guardarBusquedaParaMapa(texto: string): void {
    const mapIndex = this.mapaSeleccionado - 1;
    const key = `busqueda_vehiculos_mapa_${mapIndex}`;

    // Guardar en Map
    this.busquedaPorMapa.set(mapIndex, texto);

    // Guardar en localStorage
    if (texto) {
      localStorage.setItem(key, texto);
    } else {
      localStorage.removeItem(key);
    }
  }

  /**
   * Restaura el texto de búsqueda del mapa actual desde localStorage
   */
  private restaurarBusquedaParaMapa(): void {
    const mapIndex = this.mapaSeleccionado - 1;
    const key = `busqueda_vehiculos_mapa_${mapIndex}`;

    // Primero intentar desde el Map en memoria
    let textoGuardado = this.busquedaPorMapa.get(mapIndex);

    // Si no está en memoria, leer desde localStorage
    if (textoGuardado === undefined) {
      textoGuardado = localStorage.getItem(key) || '';
      this.busquedaPorMapa.set(mapIndex, textoGuardado);
    }

    // Actualizar el texto visible
    this.textoBusqueda = textoGuardado;

    // Aplicar la búsqueda sin llamar a onBusquedaChange (para evitar efectos secundarios)
    if (textoGuardado) {
      const terminoBusqueda = textoGuardado.toLowerCase().trim();
      const reportesFiltrados = this.filteredReportes.filter((reporte) => {
        const textoReporte = JSON.stringify(reporte).toLowerCase();
        return textoReporte.includes(terminoBusqueda);
      });

      this.coincidenciasBusqueda = reportesFiltrados.length;
      // Emitir con el mapIndex específico para este mapa
      this.busquedaVehiculos.emit({ reportes: reportesFiltrados, mapIndex });
    } else {
      // Si no hay texto guardado, mostrar todos los vehículos en el mapa actual
      this.coincidenciasBusqueda = 0;
      // Emitir con clearSearch: true para limpiar el filtro y mostrar todos
      this.busquedaVehiculos.emit({
        reportes: [],
        mapIndex,
        clearSearch: true,
      });
    }
  }

  /**
   * Actualiza el tipoMapaBase para el mapa seleccionado actualmente
   * Lee el valor guardado en localStorage para ese mapa específico
   */
  private actualizarTipoMapaBaseParaMapaSeleccionado(): void {
    if (!this.tipoMapaBaseKey) return;

    // Construir la key específica para este mapa (índice 0-based)
    const index = this.mapaSeleccionado - 1;
    const key = `${this.tipoMapaBaseKey}_${index}`;

    // Leer del localStorage
    const tipoGuardado = localStorage.getItem(key);

    if (tipoGuardado) {
      this.mapaService.setTipoMapaBase(tipoGuardado as any);
    } else {
      // Si no hay valor guardado, usar 'osm' por defecto
      this.mapaService.setTipoMapaBase('osm');
    }
  }
}
